import type { ReactNode } from 'react';
import { cx } from '../cx';
import { Skeleton } from '../Skeleton/Skeleton';
import { Table, type TableColumn } from '../Table/Table';

/** The values behind a chart, shown on request as a real table (for everyone, screen readers included). */
export type ChartTable = {
  /** What the table shows ("Open jobs by city"). */
  caption: string;
  columns: Array<{ key: string; label: ReactNode; numeric?: boolean }>;
  rows: Array<Record<string, ReactNode> & { id: string }>;
};

export type ChartFrameProps = {
  /** States the finding, not the chart type ("Four in five open jobs in Finland are fully on-site"). */
  headline: ReactNode;
  /** The measure and unit, and the period if the headline does not say it. Say so here if a y-axis does not start at zero. */
  subtitle?: ReactNode;
  /** Index and date, then the sample ("JobCrawls index, 11 Oct 2026. 19,391 open jobs in Finland."). Required. */
  source: ReactNode;
  /** The chart. */
  children?: ReactNode;
  /** A legend, when direct labels do not fit. */
  legend?: ReactNode;
  /** The table twin and the label of its toggle ("Show as table"), both translated. */
  table?: ChartTable & { toggleLabel: string };
  /**
   * `empty` replaces the chart with `empty` (below the sample minimum, say why); `loading` shows a
   * skeleton in the chart's place; `error` shows `error` (an InlineAlert with a retry).
   */
  status?: 'ready' | 'loading' | 'empty' | 'error';
  empty?: { title: ReactNode; body?: ReactNode };
  error?: ReactNode;
  headingLevel?: 2 | 3 | 4;
  /** The page's language ("fi", "sv", "en"), set on the frame so screen readers read its numbers and units in it. */
  lang?: string;
  className?: string;
};

/**
 * The frame every chart sits in (guideline 11): headline, subtitle, chart, legend, source line and
 * a "Show as table" twin. A container you read inside, flat on the page. The table toggle is a
 * native <details>, so it works on pages that do not hydrate. `data-jc-chart` scopes the forced-colours
 * and print rules in the stylesheet (jc-public-theme.css, "Chart accessibility").
 */
export function ChartFrame({
  headline,
  subtitle,
  source,
  children,
  legend,
  table,
  status = 'ready',
  empty,
  error,
  headingLevel = 3,
  lang,
  className,
}: ChartFrameProps) {
  const Heading = `h${headingLevel}` as 'h3';
  return (
    <figure
      className={cx(
        '@container m-0 flex min-w-0 flex-col gap-3 rounded-jc-container bg-jc-surface p-5 ring-1 ring-inset ring-jc-border',
        className,
      )}
      lang={lang}
      data-jc-chart=""
      aria-busy={status === 'loading' || undefined}
    >
      <div className="flex flex-col gap-1">
        <Heading className="type-subheading text-jc-ink">{headline}</Heading>
        {subtitle ? <p className="type-meta text-jc-ink-secondary">{subtitle}</p> : null}
      </div>
      {status === 'loading' ? <Skeleton className="h-40 w-full" /> : null}
      {status === 'empty' ? (
        <div className="flex flex-col items-center gap-1 rounded-jc-info bg-jc-elevated px-4 py-8 text-center">
          <p className="type-subheading text-jc-ink">{empty?.title}</p>
          {empty?.body ? <p className="type-meta text-jc-ink-secondary">{empty.body}</p> : null}
        </div>
      ) : null}
      {status === 'error' ? error : null}
      {status === 'ready' ? (
        <>
          {children}
          {legend ? (
            <div className="flex flex-wrap gap-x-4 gap-y-1 type-meta text-jc-ink-secondary">{legend}</div>
          ) : null}
        </>
      ) : null}
      <figcaption className="type-caption text-jc-ink-secondary">{source}</figcaption>
      {status === 'ready' && table ? (
        <details className="group">
          <summary className="inline-flex cursor-pointer list-none h-10 items-center rounded-jc-control px-4 text-sm font-semibold text-jc-tint hover:bg-jc-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus">
            {table.toggleLabel}
          </summary>
          <div className="mt-3">
            <Table
              caption={table.caption}
              rowKey={(r) => r.id}
              rows={table.rows}
              columns={table.columns.map((c): TableColumn<ChartTable['rows'][number]> => ({
                key: c.key,
                label: c.label,
                numeric: c.numeric,
                render: (r) => r[c.key],
              }))}
            />
          </div>
        </details>
      ) : null}
    </figure>
  );
}
